import { NEWRELIC_TABLES } from "@checkout-core/constants/NewRelicTables";
import { CleanupSource, SetDataSource, PSRMetricSource } from "@checkout-core/constants/PersistOrderDataInLocalStore";
import { WebviewState } from "@checkout-core/interfaces/WebviewRefresh";
import { recordNREvent } from "@checkout-core/newrelic";
import { LocalStorage } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { SaveUserStatePayload } from "@swiggy-private/native-bridge";
import { getUsersLastState } from "./WebviewRefresh";
import Blaze from "@swiggy-private/blaze.js";

const SWGY_PERSIST_WEBVIEW_ORDER_DATA_KEY = "swgy_persist_webview_order_data_key";

const getPersistedOrderDataFromLocalStoreFailedLogHandler = () => {
    try {
        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
            name: "getPersistedOrderDataFromLocalStoreFailed",
            // no source being called from one place which is the restoreWebviewRefreshState helper
        });
    } catch (_) {
        //no-op
    }
};

const setPersistedOrderDataFromLocalStoreFailedLogHandler = () => {
    try {
        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
            name: "setPersistedOrderDataFromLocalStoreFailed",
        });
    } catch (_) {
        //no-op
    }
};

export const getPersistedOrderDataFromLocalStore = () => {
    try {
        try {
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "getPersistedOrderDataCalled",
                // no source being called from one place which is the restoreWebviewRefreshState helper
            });
        } catch {
            // no-op
        }
        const data = LocalStorage.getItem<WebviewState>(
            SWGY_PERSIST_WEBVIEW_ORDER_DATA_KEY,
            undefined,
            getPersistedOrderDataFromLocalStoreFailedLogHandler,
        );
        if (!isEmpty(data)) {
            return data;
        }
        return null;
    } catch (err) {
        try {
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "getPersistedOrderDataFailed",
                errorMessage: JSON.stringify(err),
            });
        } catch {
            // no-op
        }
    }
};

export const setPersistOrderDataInLocalStorage = (payload: SaveUserStatePayload, source?: SetDataSource) => {
    try {
        try {
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "setPersistOrderDataCalled",
                source: source || "",
            });
        } catch {
            // no-op
        }
        if (!isEmpty(payload)) {
            LocalStorage.setItem(
                SWGY_PERSIST_WEBVIEW_ORDER_DATA_KEY,
                payload,
                setPersistedOrderDataFromLocalStoreFailedLogHandler,
            );
        }
    } catch (err) {
        try {
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "setPersistOrderDataFailed",
                errorMessage: JSON.stringify(err),
                source: source || "",
            });
        } catch {
            // no-op
        }
    }
};

export const cleanupPersistOrderDataInLocalStorage = (source?: CleanupSource) => {
    try {
        try {
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "cleanupPersistOrderDataCalled",
                source: source || "",
            });
        } catch {
            // no-op
        }
        LocalStorage.removeItem(SWGY_PERSIST_WEBVIEW_ORDER_DATA_KEY);
    } catch (err) {
        try {
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "cleanupPersistOrderDataFailed",
                errorMessage: JSON.stringify(err),
                source: source || "",
            });
        } catch {
            // no-op
        }
    }
};

export const isWebviewRefreshDataPresentInStore = () => {
    try {
        const appWebviewState = getUsersLastState();
        const localStorageWebviewState = getPersistedOrderDataFromLocalStore();
        // both of the states being present should be treated as webview refresh being present
        return !isEmpty(appWebviewState) && !isEmpty(localStorageWebviewState);
    } catch {
        // no-op
        return false;
    }
};

export const trackPSRMetricsForWebviewRefresh = (name: PSRMetricSource, payload: Record<string, string> = {}) => {
    try {
        try {
            Blaze.recordStart(NEWRELIC_TABLES.WEBVIEW_REFRESH_PSR);
        } catch {
            // no-op
        }

        const isLocalStorageWebviewStatePresent = isWebviewRefreshDataPresentInStore();

        try {
            Blaze.recordEnd(NEWRELIC_TABLES.WEBVIEW_REFRESH_PSR);
        } catch {
            // no-op
        }

        if (!isLocalStorageWebviewStatePresent) {
            return;
        }

        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_PSR,
            name,
            ...(payload || {}),
        });
    } catch {
        // no-op
    }
};
